import axios from 'axios';
import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaSpinner, FaImage, FaEdit, FaSave, FaTimes } from "react-icons/fa";
import AdminPageLoader from './AdminPageLoader';

const API_BASE_URL = process.env.REACT_APP_API_URL;

function EditBrandPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [brandData, setBrandData] = useState({ name: '', image: '' });
    const [imageUploading, setImageUploading] = useState(false);
    const [removingImage, setRemovingImage] = useState(false);
    const [imageLoadError, setImageLoadError] = useState(false);
    const [savingBrand, setSavingBrand] = useState(false);
    const [loading, setLoading] = useState(true);
    const token = localStorage.getItem('authToken');

    const handleApiError = (error) => {
        console.error('API Error:', error);
        alert(error.response?.data?.message || 'Something went wrong. Please try again.');
    };

    const handleBrandData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/single-brand-data/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBrandData(response.data);
            console.log(response.data);
        } catch (error) {
            handleApiError(error);
            setTimeout(() => navigate('/admin/brands'), 2000);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (event) => {
        setBrandData({
            ...brandData,
            [event.target.name]: event.target.value,
        });
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
        if (!allowedTypes.includes(file.type)) {
            alert('Only JPG, PNG, WebP, or AVIF images are allowed.');
            return;
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB limit
            alert('File size must be under 5MB.');
            return;
        }

        try {
            setImageUploading(true);
            const formData = new FormData();
            formData.append('image', file);

            const response = await axios.post(`${API_BASE_URL}/upload-prod-img`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBrandData(prev => ({ ...prev, image: response.data.path }));
            setImageLoadError(false);
            alert('Image uploaded successfully!');
        } catch (error) {
            handleApiError(error);
        } finally {
            setImageUploading(false);
        }
    };

    const handleRemoveImage = async () => {
        if (!window.confirm('Are you sure you want to remove this image?')) return;

        try {
            setRemovingImage(true);
            const response = await axios.delete(`${API_BASE_URL}/remove-brand-image/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBrandData(prev => ({ ...prev, image: '' }));
            setImageLoadError(false);
            alert(response.data.message || 'Image removed successfully!');
        } catch (error) {
            handleApiError(error);
        } finally {
            setRemovingImage(false);
        }
    };

    const validateFields = () => {
        if (!brandData.name.trim()) {
            alert('Brand name is required');
            return false;
        }
        if (!brandData.image.trim()) {
            alert('Brand image is required');
            return false;
        }
        return true;
    };

    const handleUpdateBrand = async () => {
        if (!validateFields()) return;

        if (!brandData.image) {
            alert('Brand image is required');
            return;
        }

        try {
            setSavingBrand(true);
            const response = await axios.put(`${API_BASE_URL}/update-brand/${id}`, brandData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message);
            navigate('/admin/brands');
        } catch (error) {
            handleApiError(error);
        } finally {
            setSavingBrand(false);
        }
    };

    const handleBackButtonClick = () => {
        navigate('/admin/brands');
    };

    useEffect(() => {
        handleBrandData();
    }, [id]);

    if (loading) {
        return (
            <div className="container-fluid p-0 g-0 adma">
                <div className="row p-3 pb-2 g-0" style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
                    <div className="col-12">
                        <div className="section-title">
                            <h4 className="mt-1" style={{ fontSize: '16px', fontWeight: '700' }}>EDIT BRAND</h4>
                            <p className="text-muted p-0 m-0" style={{ fontSize: '12px' }}>Update brand information and image</p>
                        </div>
                    </div>
                </div>
                <div className="p-3">
                    <AdminPageLoader message="Loading brand details..." />
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid p-0 g-0 adma">
                <div className="row p-3 pb-2 g-0" style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1" style={{ fontSize: '16px', fontWeight: '700' }}>EDIT BRAND</h4>
                                <p className="text-muted p-0 m-0" style={{ fontSize: '12px' }}>Update brand information and image</p>
                            </div>
                            <button 
                                className="btn theme-btn-blue-flat btn-sm rounded-0"
                                onClick={handleBackButtonClick}
                                title="Back to Brands"
                                style={{
                                    padding: '6px 16px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                <FaArrowLeft size={11} />
                                Back to Brands
                            </button>
                        </div>
                    </div>
                </div>

                <div className="row g-0 details-content-row">
                    <div className="col-12">
                        <div className="card rounded-0" style={{ border: 'none', borderBottom: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <div className="card-header rounded-0" style={{ backgroundColor: '#e8eaf6', borderBottom: '1px solid #d1d5db', padding: '12px 24px', margin: 0, display: 'flex', alignItems: 'center' }}>
                                <h5 className="mb-0" style={{ color: '#1a237e', fontWeight: '700', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'flex', alignItems: 'center', margin: 0 }}>
                                    <FaEdit className="me-2" style={{ fontSize: '14px' }} />
                                    Brand Information
                                </h5>
                            </div>
                            <div className="card-body p-0">
                                <div className="row g-0">
                                    {/* Left Column: Form Inputs */}
                                    <div className="col-md-6 p-4">
                                        <div className="mb-3">
                                            <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#374151' }}>Brand Name</label>
                                            <input
                                                type="text"
                                                className="form-control rounded-0"
                                                style={{ border: '1px solid #cbd5e1', height: '38px', fontSize: '13px' }}
                                                name="name"
                                                value={brandData.name || ''}
                                                onChange={handleChange}
                                                placeholder="Enter brand name"
                                            />
                                        </div>
                                        
                                        <div className="mb-4">
                                            <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#374151' }}>Brand Image</label>
                                            <input
                                                type="file"
                                                className="form-control rounded-0"
                                                style={{ border: '1px solid #cbd5e1', fontSize: '13px', padding: '7px 12px' }}
                                                name="image"
                                                onChange={handleImageUpload}
                                                accept="image/*"
                                            />
                                            {imageUploading && (
                                                <div className="mt-2 d-flex align-items-center gap-1">
                                                    <FaSpinner className="spinner-icon" style={{ fontSize: '11px', color: '#94a3b8' }} />
                                                    <small style={{ color: '#94a3b8', fontSize: '11px' }}>Uploading image...</small>
                                                </div>
                                            )}
                                            {brandData.image && !imageUploading && (
                                                <div className="mt-2 d-flex align-items-center gap-1">
                                                    <FaImage style={{ fontSize: '11px', color: '#16a34a' }} />
                                                    <small style={{ color: '#16a34a', fontSize: '11px' }}>Image uploaded successfully</small>
                                                </div>
                                            )}
                                        </div>

                                        <button
                                            className="btn theme-btn-blue rounded-0 w-100"
                                            style={{
                                                height: '38px',
                                                fontSize: '13px',
                                                fontWeight: '600',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px'
                                            }}
                                            disabled={savingBrand || imageUploading}
                                            onClick={handleUpdateBrand}
                                        >
                                            {savingBrand ? (
                                                <><FaSpinner className="spinner-icon" style={{ fontSize: '11px' }} /> Updating Brand...</>
                                            ) : (
                                                <><FaSave size={13} /> Update Brand</>
                                            )}
                                        </button>
                                    </div>
                                    
                                    {/* Right Column: Image Preview */}
                                    <div className="col-md-6 p-4 border-start" style={{ borderColor: '#e2e8f0' }}>
                                        <div className="text-center h-100 d-flex flex-column">
                                            <label className="form-label fw-bold d-block text-start" style={{ fontSize: '12px', color: '#374151', marginBottom: '8px' }}>Current Image</label>
                                            <div className="brand-preview-container flex-grow-1">
                                                {brandData.image && !imageLoadError ? (
                                                    <div className="image-wrapper w-100 h-100 d-flex align-items-center justify-content-center">
                                                        <img
                                                            src={`${API_BASE_URL}/${brandData.image}`}
                                                            alt="Brand Preview"
                                                            className="brand-preview-image"
                                                            onError={() => setImageLoadError(true)}
                                                        />
                                                        <button
                                                            className="remove-image-btn"
                                                            onClick={handleRemoveImage}
                                                            disabled={removingImage || imageUploading || savingBrand}
                                                            title="Remove image"
                                                        >
                                                            {removingImage ? (
                                                                <FaSpinner className="spinner-icon" />
                                                            ) : (
                                                                <FaTimes />
                                                            )}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="brand-preview-placeholder">
                                                        <p className="text-muted mb-0" style={{ fontSize: '13px' }}>No image uploaded</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .brand-preview-container {
                    border: 1px solid #cbd5e1;
                    border-radius: 0;
                    padding: 20px;
                    min-height: 220px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f8fafc;
                    position: relative;
                }
                
                .image-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 100%;
                    height: 100%;
                }
                
                .image-wrapper:hover .remove-image-btn {
                    opacity: 1;
                }
                
                .brand-preview-image {
                    max-width: 100%;
                    max-height: 200px;
                    object-fit: contain;
                    border-radius: 0;
                    border: 1px solid #e2e8f0;
                }
                
                .remove-image-btn {
                    position: absolute;
                    top: 10px;
                    right: 10px;
                    width: 28px;
                    height: 28px;
                    border-radius: 0;
                    background: #dc3545;
                    color: white;
                    border: none;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    opacity: 0;
                    transition: all 0.2s ease;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                    font-size: 13px;
                }
                
                .remove-image-btn:hover:not(:disabled) {
                    background: #bd2130;
                    transform: scale(1.05);
                }
                
                .remove-image-btn:disabled {
                    background: #6c757d;
                    cursor: not-allowed;
                }
                
                .spinner-icon {
                    animation: spin 1s linear infinite;
                }
                
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                .brand-preview-placeholder {
                    text-align: center;
                    color: #64748b;
                }
                
                .card {
                    border-radius: 0;
                }
                
                .form-control:focus {
                    border-color: #1a237e;
                    box-shadow: none;
                }
            `}</style>
        </Fragment>
    );
}

export default EditBrandPage;
